import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ChangeStepService } from '@app/core/components/stepper/service/change-step.service';
import { ChangeDeviceService } from '@app/shared/services/change-device.service';
import { SharedModule } from '@app/shared/shared.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { Country } from '@app/shared/interfaces/common-data/country';
import { AddressDataService } from '@app/shared/services/common-data/address-data.service';
import { SaveDataService } from '@app/shared/services/save-data.service';
import { CoreModule } from '@app/core/core.module';
import { AppService } from '@app/app.service';
import { PrimeNGConfig } from 'primeng/api';

@Component({
    selector: 'app-personal-data-birth',
    standalone: true,
    imports: [CoreModule, CommonModule, SharedModule, TranslateModule],
    templateUrl: './personal-data-birth.component.html',
    styleUrl: './personal-data-birth.component.scss',
})
export class PersonalDataBirthComponent implements AfterViewInit{
    continueButton = 'common.buttons.continueButton';
    backButton = 'common.buttons.backButton';

    personalDataTitle = 'features.personalData.personalDataTitle';
    personalDataBirthSubtitle = 'features.personalData.personalDataBirth.personalDataBirthSubtitle';
    countryFloatLabel = 'features.personalData.personalDataBirth.countryFloatLabel';
    personalDataDate = 'features.personalData.personalDataBirth.date';
    birthDateLabel = 'features.personalData.personalDataBirth.birthDateLabel';

    backButtonDisabled = false;
    continueButtonDisabled = true;

    isPortraitDevice = false;

    formGroup!: FormGroup;

    selectedCountry?: Country;
    selectedDate: any;
    countries: Country[] = [];

    private appData = inject(AppService);
    date: any;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private changeDeviceService: ChangeDeviceService,
        private changeStepService: ChangeStepService,
        private fb: FormBuilder,
        private addressDataService: AddressDataService,
        private saveDataService: SaveDataService,
        private primengConfig: PrimeNGConfig,
        private translateService: TranslateService
    ) {
        this.changeStepService.changeStep(4);
    }

    ngOnInit() {
        this.isPortraitDevice = this.changeDeviceService.isPortraitDevice;
        this.changeDeviceService.changedDevice.subscribe((value) => {
            this.isPortraitDevice = value;
        });

        this.createForm();
        this.setValuesForm(this.appData.data.userData.personalDataBirth);
        this.getCountries();

        this.formIsValid();
    }

    createForm() {

        this.formGroup = this.fb.group({
            birthDate: new FormControl('', [Validators.required, this.ageValidator()], ),
            country: new FormControl(this.selectedCountry ? this.selectedCountry.code : '', [Validators.required, this.countryValidator()]),
        });
    }

    //-------------------------//
    //----------SETS-----------//
    //-------------------------//

    setValuesForm(value?: any) {
        if (value != undefined) {

            this.selectedCountry = {
                code: value.country.code ? value.country.code : '',
                name: value.country.name ? value.country.name : '',
                regime: value.country.regime ? value.country.regime : '',
            };

            this.formGroup = this.fb.group({
                birthDate: new FormControl(value.birthDate ? value.birthDate : '', [Validators.required, this.ageValidator()]),
                country: new FormControl(this.selectedCountry, [Validators.required, this.countryValidator()]),
            });
        }
    }

    getCountries() {
        this.addressDataService.getCountries().subscribe({
            next: (response) => {
                this.countries = response.sort((a, b) => (a.name < b.name ? -1 : 1));
                this.countries = this.countries.sort((a) => (a.name === 'España' ? -1 : 0));


                if(this.selectedCountry!.code){
                    this.selectedCountry = this.findCountryByCode(this.selectedCountry!.code);
                    this.formGroup.patchValue({
                        country: this.selectedCountry
                    });
                }
            },
            error: (error) => console.log(error),
        });
    }

    findCountryByCode(code : string): Country | undefined{
        if (!this.countries)
            return undefined;
        return this.countries.find(country => country.code === code);
    }

    // Traducimos los calendarios
    ngAfterViewInit() {
        this.translateChange('es')
    }

    translateChange(lang: string) {
        this.translateService.use(lang)
        this.translateService.get('primeng').subscribe((res) => this.primengConfig.setTranslation(res))
    }

    //-------------------------//
    //---------ON CHANGE-------//
    //-------------------------//

    onChangeCountry(event: DropdownChangeEvent) {
        this.selectedCountry = event.value;
        // this.formGroup.patchValue({
        //     country: new FormControl(this.selectedCountry, [Validators.required, this.countryValidator()])
        // });
        this.formIsValid();
    }

    onChangeDate(event: CalendarModule) {
        this.formIsValid();
    }

    //-------------------------//
    //-------VALIDATION--------//
    //-------------------------//

    ageValidator(): ValidatorFn { return  (control: AbstractControl):
        { [key:string]: any } | null => {

            if(control.value == ""){
                return null;
            }

            const val = new Date (control.value);
            const age = this.calculateAge(val);

            if (age < 18) {
                return { edadInvalida : true};
            }

            return null;
        }
    }

    countryValidator(): ValidatorFn { return  (control: AbstractControl):
        { [key:string]: any } | null => {

            if(!control || control.value == "" || control.value == null || control.value.code == "") {
                return { paisInvalid : true};
            }

            return null;
        }
    }

    calculateAge(date : Date) : number {

        const today = new Date();

        let age = today.getFullYear() - date.getFullYear();

        const month = today.getMonth() - date.getMonth();

        if(month < 0 || (month === 0 && today.getDate() < date.getDate())) {
            age --;
        }

        return age;
    }

    formIsValid() {
        this.continueButtonDisabled = !this.formGroup.valid;
    }

    //-------------------------//
    //--------NAVIGATION-------//
    //-------------------------//

    onClicButtonBack() {
        this.saveDataService.saveDataLocal(this.formGroup.value, 'personal-data-birth');
        this.router.navigate(['/main/step/personal-data-info'], { relativeTo: this.route });
    }

    onClicButtonContinue() {
        this.saveDataService.saveDataLocal(this.formGroup.value, 'personal-data-birth');
        this.router.navigate(['/main/step/contact-data-info'], { relativeTo: this.route });
    }

    ngOnDestroy() {}
}
